import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZoomIn, ZoomOut, Loader2, Check } from 'lucide-react';

// Produce a square, downscaled PNG data URL from the selected crop area.
async function getCroppedDataUrl(imageSrc, cropPixels, outSize = 256) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = outSize;
  canvas.height = outSize;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, outSize, outSize,
  );
  return canvas.toDataURL('image/png');
}

export function IconCropper({ open, imageSrc, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPx) => setAreaPixels(areaPx), []);

  const confirm = async () => {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const dataUrl = await getCroppedDataUrl(imageSrc, areaPixels, 256);
      onCropped(dataUrl);
    } finally {
      setBusy(false);
    }
  };

  const handleOpenChange = (o) => { if (!o) onCancel(); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-[color:var(--cx-surface-solid)] border-[color:var(--cx-border)]" data-testid="icon-cropper-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Crop icon</DialogTitle>
          <DialogDescription>Zoom and drag to frame your icon inside the square.</DialogDescription>
        </DialogHeader>

        <div className="relative w-full h-64 rounded-xl overflow-hidden bg-[color:var(--cx-bg-elev,#0b0f14)] border border-[color:var(--cx-border)]" data-testid="icon-cropper-area">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              minZoom={1}
              maxZoom={4}
              restrictPosition
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="flex items-center gap-3 px-1 pt-1">
          <ZoomOut className="h-4 w-4 text-[color:var(--cx-muted)] shrink-0" />
          <Slider
            min={1} max={4} step={0.01}
            value={[zoom]}
            onValueChange={(v) => setZoom(v[0])}
            data-testid="icon-cropper-zoom"
            aria-label="Zoom"
          />
          <ZoomIn className="h-4 w-4 text-[color:var(--cx-muted)] shrink-0" />
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="button" onClick={confirm} disabled={busy || !areaPixels} data-testid="icon-cropper-confirm">
            {busy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cropping…</>) : (<><Check className="h-4 w-4 mr-1.5" />Use icon</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
